import type {
    RenderBackend,
    RendererOptions,
    VideoProcessor,
} from '@pexip/media-processor';
import {isRenderEffects} from '@pexip/media-processor';
import type {
    InputConstraintSet,
    MediaDeviceRequest,
} from '@pexip/media-control';
import {
    extractConstraints,
    getValueFromConstrainNumber,
} from '@pexip/media-control';
import {hasOwn, isEmpty} from '@pexip/utils';

import type {
    ExtendedMediaTrackSettings,
    MediaSignals,
    TrackProcessor,
    VideoContentHint,
    VideoRenderParams,
} from './types';
import {createMediaTrack, getBlurKernelSize, getTentBlurSize} from './utils';
import {logger, proxyWithLog} from './logger';
import {isVideoContentHint} from './typeGuard';
import {PROCESSOR_LABELS} from './constants';

interface VideoStreamProcessOptions extends Partial<VideoRenderParams> {
    /**
     * Whether or to enable this processor
     */
    shouldEnable: () => boolean;
    getVideoProcessor: () => VideoProcessor;
    referenceProcessingHeight?: number;
    processingWidth: number;
    processingHeight: number;
    width?: number;
    height?: number;
    label?: string;
    stopAsMute?: () => boolean;
    dynamicProcessingDimensions?: () => boolean;
    gpuAPI?: () => RenderBackend;
    signals?: MediaSignals;
}

interface VideoStreamProcessProps extends Partial<VideoRenderParams> {
    contentHint?: VideoContentHint;
}

const FEATURE_KEYS = {
    backgroundBlurAmount: 'number',
    backgroundImageUrl: 'string',
    backgroundThreshold: 'number',
    maskCombineRatio: 'number',
    edgeBlurAmount: 'number',
    foregroundThreshold: 'number',
    frameRate: 'number',
    videoSegmentation: 'string',
    width: 'number',
    height: 'number',
    pan: 'boolean',
    tilt: 'boolean',
    zoom: 'boolean',
    contentHint: 'string',
    sigmaSpace: 'number',
    sigmaRangeLo: 'number',
    sigmaRangeHi: 'number',
    excludeBystanders: 'boolean',
    personCenterX: 'number',
    personCenterY: 'number',
    morphErodeRadiusPx: 'number',
    morphPass: 'number',
    morphDilateRadiusPx: 'number',
    lightWrapIntensity: 'number',
    lightWrapTightness: 'number',
    lightWrapEdgeBand: 'number',
    lightWrapBlurAmount: 'number',
    downSampleFactor: 'number',
} as const;

type FeaturePropKeys = keyof typeof FEATURE_KEYS;
type FeatureProps = Pick<Partial<VideoStreamProcessProps>, FeaturePropKeys>;

export const updateFeatureProps = (
    constraints: MediaDeviceRequest['video'],
    props: FeatureProps,
) => {
    const features: FeatureProps = {};
    if (constraints === undefined || typeof constraints === 'boolean') {
        return features;
    }

    for (const k of Object.keys(constraints)) {
        const key = k as FeaturePropKeys;
        if (!FEATURE_KEYS[key]) {
            continue;
        }
        switch (key) {
            case 'contentHint': {
                const [[feature = ''] = []] =
                    extractConstraints(key)(constraints);
                if (isVideoContentHint(feature) && props[key] !== feature) {
                    props[key] = feature;
                    features[key] = feature;
                }
                break;
            }
            case 'videoSegmentation': {
                const [[feature] = []] = extractConstraints(key)(constraints);
                if (isRenderEffects(feature) && props[key] !== feature) {
                    props[key] = feature;
                    features[key] = feature;
                }
                break;
            }
            case 'backgroundImageUrl': {
                const [[feature] = []] = extractConstraints(key)(constraints);
                if (feature) {
                    props[key] = feature;
                    features[key] = feature;
                }
                break;
            }
            case 'backgroundBlurAmount':
            case 'edgeBlurAmount':
            case 'lightWrapBlurAmount': {
                const [feature] = extractConstraints(key)(constraints);
                if (feature !== undefined) {
                    const value = getValueFromConstrainNumber(feature);
                    if (props[key] !== value) {
                        props[key] = value;
                        features[key] = value;
                    }
                }
                break;
            }
            case 'backgroundThreshold':
            case 'downSampleFactor':
            case 'foregroundThreshold':
            case 'frameRate':
            case 'height':
            case 'lightWrapEdgeBand':
            case 'lightWrapIntensity':
            case 'lightWrapTightness':
            case 'maskCombineRatio':
            case 'morphDilateRadiusPx':
            case 'morphErodeRadiusPx':
            case 'morphPass':
            case 'personCenterX':
            case 'personCenterY':
            case 'sigmaRangeHi':
            case 'sigmaRangeLo':
            case 'sigmaSpace':
            case 'width': {
                const [feature] = extractConstraints(key)(constraints);
                if (feature !== undefined) {
                    const value = getValueFromConstrainNumber(feature);
                    if (props[key] !== value) {
                        props[key] = value;
                        features[key] = value;
                    }
                }
                break;
            }
            case 'pan':
            case 'tilt':
            case 'zoom': {
                const [feature] = extractConstraints(key)(constraints);
                if (feature !== undefined && props[key] !== feature) {
                    props[key] = feature;
                    features[key] = feature;
                }
                break;
            }
            default:
                break;
        }
    }
    return features;
};

export const mapFeatureToSettings = (props: InputConstraintSet) => {
    return new Proxy<ExtendedMediaTrackSettings>(
        {},
        {
            get(_target, p: keyof FeatureProps, receiver) {
                switch (p) {
                    case 'tilt':
                    case 'zoom':
                    case 'pan': {
                        if (props[p] === undefined || props[p] === false) {
                            return undefined;
                        }
                        return 0;
                    }
                    default: {
                        return Reflect.get(props, p, receiver);
                    }
                }
            },
            has(_target, p) {
                return p in props;
            },
            ownKeys() {
                return Reflect.ownKeys(props);
            },
            getOwnPropertyDescriptor(_target, p) {
                if (hasOwn(props, p)) {
                    return {
                        configurable: true,
                        enumerable: true,
                        writable: true,
                    };
                }
                return undefined;
            },
        },
    );
};
export const mapSettingsToFeatures = (settings: ExtendedMediaTrackSettings) => {
    return new Proxy<InputConstraintSet>(
        {},
        {
            get(_target, p: keyof FeatureProps, receiver) {
                switch (p) {
                    case 'tilt':
                    case 'zoom':
                    case 'pan': {
                        if (settings[p] === undefined) {
                            return undefined;
                        }
                        return true;
                    }
                    default: {
                        return Reflect.get(settings, p, receiver);
                    }
                }
            },
            has(_target, p) {
                return p in settings;
            },
            ownKeys() {
                return Reflect.ownKeys(settings);
            },
            getOwnPropertyDescriptor(target, p) {
                if (hasOwn(settings, p)) {
                    return {
                        configurable: true,
                        enumerable: true,
                        writable: true,
                        value: this.get?.(target, p, undefined),
                    };
                }
                return undefined;
            },
        },
    );
};

const applyFeatures = async (
    processor: VideoProcessor,
    features: FeatureProps,
    referenceProcessingHeight?: number,
) => {
    const renderOptions: Partial<RendererOptions> = {};
    for (const key in features) {
        const k = key as keyof typeof features;
        switch (k) {
            case 'maskCombineRatio':
            case 'foregroundThreshold':
            case 'sigmaSpace':
            case 'sigmaRangeLo':
            case 'sigmaRangeHi':
            case 'morphErodeRadiusPx':
            case 'morphPass':
            case 'morphDilateRadiusPx':
            case 'lightWrapIntensity':
            case 'lightWrapTightness':
            case 'lightWrapEdgeBand':
            case 'downSampleFactor':
            case 'backgroundThreshold': {
                const value = features[k];
                if (value !== undefined) {
                    renderOptions[k] = value;
                }
                break;
            }
            case 'excludeBystanders': {
                const value = features[k];
                if (value !== undefined) {
                    renderOptions[k] = value;
                }
                break;
            }
            case 'personCenterX': {
                const value = features[k];
                if (value !== undefined) {
                    renderOptions.personCenter = [
                        value,
                        renderOptions.personCenter?.[1] ??
                            processor.renderOptions.personCenter[1],
                    ];
                }
                break;
            }
            case 'personCenterY': {
                const value = features[k];
                if (value !== undefined) {
                    renderOptions.personCenter = [
                        renderOptions.personCenter?.[0] ??
                            processor.renderOptions.personCenter[0],
                        value,
                    ];
                }
                break;
            }
            case 'lightWrapBlurAmount':
            case 'backgroundBlurAmount': {
                const value = features[k];
                if (value !== undefined) {
                    renderOptions[k] = getBlurKernelSize(
                        value,
                        referenceProcessingHeight ?? processor.processingHeight,
                        processor.processingHeight,
                    );
                }
                break;
            }
            case 'edgeBlurAmount': {
                const value = features[k];
                if (value !== undefined) {
                    renderOptions[k] = getTentBlurSize(
                        value,
                        referenceProcessingHeight ?? processor.processingHeight,
                        processor.processingHeight,
                    );
                }
                break;
            }
            case 'videoSegmentation': {
                const value = features[k];
                if (value && value !== processor.renderOptions.effects) {
                    renderOptions.effects = value;
                }
                break;
            }
            case 'backgroundImageUrl': {
                const value = features[k];
                if (
                    value &&
                    value !== processor.renderOptions.backgroundImageUrl
                ) {
                    renderOptions.backgroundImageUrl = value;
                }
                break;
            }
            default: {
                break;
            }
        }
    }
    if (!isEmpty(renderOptions)) {
        await processor.update({renderOptions});
    }
};

export const createVideoStreamProcess = ({
    backgroundImageUrl,
    dynamicProcessingDimensions = () => false,
    backgroundThreshold,
    downSampleFactor,
    foregroundThreshold,
    lightWrapEdgeBand,
    lightWrapIntensity,
    lightWrapTightness,
    morphDilateRadiusPx,
    morphErodeRadiusPx,
    morphPass,
    personCenterX,
    personCenterY,
    sigmaSpace,
    sigmaRangeHi,
    sigmaRangeLo,
    frameRate,
    gpuAPI = () => 'webgl', // dynamically changing the config will not be picked up, not a priority for now
    label = PROCESSOR_LABELS.VideoProcessor,
    referenceProcessingHeight,
    maskCombineRatio,
    processingHeight,
    processingWidth,
    shouldEnable,
    stopAsMute = () => false,
    videoSegmentation,
    signals,
    getVideoProcessor,
    ...options
}: VideoStreamProcessOptions): TrackProcessor => {
    const backgroundBlurAmount =
        options.backgroundBlurAmount &&
        getBlurKernelSize(
            options.backgroundBlurAmount,
            referenceProcessingHeight ?? processingHeight,
            processingHeight,
        );
    const lightWrapBlurAmount =
        options.lightWrapBlurAmount &&
        getBlurKernelSize(
            options.lightWrapBlurAmount,
            referenceProcessingHeight ?? processingHeight,
            processingHeight,
        );
    const edgeBlurAmount =
        options.edgeBlurAmount &&
        getTentBlurSize(
            options.edgeBlurAmount,
            referenceProcessingHeight ?? processingHeight,
            processingHeight,
        );
    const props: VideoStreamProcessProps = {
        videoSegmentation,
        backgroundBlurAmount,
        backgroundImageUrl,
        backgroundThreshold,
        downSampleFactor,
        edgeBlurAmount,
        foregroundThreshold,
        frameRate,
        lightWrapBlurAmount,
        lightWrapEdgeBand,
        lightWrapIntensity,
        lightWrapTightness,
        maskCombineRatio,
        morphDilateRadiusPx,
        morphErodeRadiusPx,
        morphPass,
        personCenterX,
        personCenterY,
        sigmaRangeHi,
        sigmaRangeLo,
        sigmaSpace,
    };

    return async prevMediaTrack => {
        const features = updateFeatureProps(
            prevMediaTrack.getConstraints(),
            props,
        );
        const shouldEnabled = shouldEnable();
        if (!shouldEnabled || !prevMediaTrack.track || prevMediaTrack.stopped) {
            logger.debug(
                {
                    label,
                    features,
                    shouldEnabled,
                },
                'Video processing is skipped',
            );
            return prevMediaTrack;
        }
        const {videoSegmentation, ...renderOptions} = props;

        const videoProcessor = proxyWithLog(logger, label)(
            getVideoProcessor(),
            'VideoProcessor',
        );

        if (dynamicProcessingDimensions()) {
            const {width, height} = prevMediaTrack.getSettings();
            if (props.backgroundBlurAmount !== undefined) {
                renderOptions.backgroundBlurAmount = getBlurKernelSize(
                    props.backgroundBlurAmount,
                    referenceProcessingHeight ?? processingHeight,
                    height ?? processingHeight,
                );
            }
            if (props.lightWrapBlurAmount !== undefined) {
                renderOptions.lightWrapBlurAmount = getBlurKernelSize(
                    props.lightWrapBlurAmount,
                    referenceProcessingHeight ?? processingHeight,
                    height ?? processingHeight,
                );
            }
            if (props.edgeBlurAmount !== undefined) {
                renderOptions.edgeBlurAmount = getTentBlurSize(
                    props.edgeBlurAmount,
                    referenceProcessingHeight ?? processingHeight,
                    height ?? processingHeight,
                );
            }
            await videoProcessor.update({
                processingWidth: width,
                processingHeight: height,
                renderOptions: {
                    backend: gpuAPI(),
                    ...renderOptions,
                    effects: videoSegmentation,
                },
            });
        } else {
            await applyFeatures(
                videoProcessor,
                {...features, videoSegmentation},
                referenceProcessingHeight,
            );
        }
        const track = await videoProcessor.process(prevMediaTrack.track);
        // Inherit contentHint from previous track
        track.contentHint = prevMediaTrack.track.contentHint;

        let trackReleaseTimeoutID = 0;

        return createMediaTrack({
            label,
            kind: 'videoinput',
            get constraints() {
                return this.previousMediaTrack?.getConstraints();
            },
            get previousMediaTrack() {
                return prevMediaTrack;
            },
            get input() {
                return this.previousMediaTrack?.input;
            },
            get expectedInput() {
                return this.previousMediaTrack?.expectedInput;
            },
            track,
            overrideMute: true,
            get muted() {
                return this.previousMediaTrack?.muted;
            },
            mute(mute, _self, soft) {
                videoProcessor.mute(mute);
                track.enabled = !mute;
                if (stopAsMute() && !soft) {
                    if (mute) {
                        // Track cannot be stopped immediately as we need to ensure
                        // the last frame is black to avoid frozen frame in a gateway call
                        trackReleaseTimeoutID = window.setTimeout(() => {
                            track.stop();
                            void videoProcessor.close();
                            void this?.release?.();
                            trackReleaseTimeoutID = 0;
                        }, 500);
                    } else {
                        if (trackReleaseTimeoutID) {
                            window.clearTimeout(trackReleaseTimeoutID);
                            trackReleaseTimeoutID = 0;
                        }
                    }
                }
                this.previousMediaTrack?.mute(mute, soft);
            },
            signals,
            async release() {
                await videoProcessor.close();
                await this.previousMediaTrack?.release();
            },
            async applyConstraints(constraints) {
                if (isEmpty(constraints)) {
                    return;
                }
                const features = updateFeatureProps(constraints, props);
                logger.debug(
                    {label, constraints: constraints, features},
                    'apply video constraints',
                );
                if (isEmpty(features)) {
                    return;
                }
                await applyFeatures(
                    videoProcessor,
                    features,
                    referenceProcessingHeight,
                );
                if (dynamicProcessingDimensions()) {
                    const acceptedSettings =
                        this.previousMediaTrack?.source.getSettings();
                    if (acceptedSettings?.width && acceptedSettings?.height) {
                        const renderOptions: Partial<RendererOptions> = {};
                        if (props.backgroundBlurAmount !== undefined) {
                            renderOptions.backgroundBlurAmount =
                                getBlurKernelSize(
                                    props.backgroundBlurAmount,
                                    referenceProcessingHeight ??
                                        processingHeight,
                                    acceptedSettings.height,
                                );
                        }
                        if (props.lightWrapBlurAmount !== undefined) {
                            renderOptions.lightWrapBlurAmount =
                                getBlurKernelSize(
                                    props.lightWrapBlurAmount,
                                    referenceProcessingHeight ??
                                        processingHeight,
                                    acceptedSettings.height,
                                );
                        }
                        if (props.edgeBlurAmount !== undefined) {
                            renderOptions.edgeBlurAmount = getTentBlurSize(
                                props.edgeBlurAmount,
                                referenceProcessingHeight ?? processingHeight,
                                acceptedSettings.height,
                            );
                        }
                        await videoProcessor.update({
                            processingHeight: acceptedSettings.height,
                            processingWidth: acceptedSettings.width,
                            renderOptions,
                        });
                    }
                }
            },
            getSettings: () => {
                const contentHint =
                    (track.contentHint as VideoContentHint) ?? '';
                const ro = videoProcessor.renderOptions;
                return {
                    backgroundBlurAmount: props.backgroundBlurAmount,
                    backgroundImageUrl: ro.backgroundImageUrl,
                    backgroundThreshold: ro.backgroundThreshold,
                    contentHint,
                    downSampleFactor: ro.downSampleFactor,
                    edgeBlurAmount: ro.edgeBlurAmount,
                    excludeBystanders: ro.excludeBystanders,
                    foregroundThreshold: ro.foregroundThreshold,
                    frameRate: props.frameRate,
                    height: props.height,
                    lightWrapBlurAmount: ro.lightWrapBlurAmount,
                    lightWrapEdgeBand: ro.lightWrapEdgeBand,
                    lightWrapIntensity: ro.lightWrapIntensity,
                    lightWrapTightness: ro.lightWrapTightness,
                    maskCombineRatio: ro.maskCombineRatio,
                    morphDilateRadiusPx: ro.morphDilateRadiusPx,
                    morphErodeRadiusPx: ro.morphErodeRadiusPx,
                    morphPass: ro.morphPass,
                    panEnabled: props.pan,
                    personCenterX: ro.personCenter[0],
                    personCenterY: ro.personCenter[1],
                    sigmaRangeHi: ro.sigmaRangeHi,
                    sigmaRangeLo: ro.sigmaRangeLo,
                    sigmaSpace: ro.sigmaSpace,
                    tiltEnabled: props.tilt,
                    videoSegmentation: ro.effects,
                    width: props.width,
                    zoomEnabled: props.zoom,
                };
            },
        });
    };
};
